import { useEffect, useState, type ReactNode } from 'react';
import { BrowserRouter, Link, Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { useAuth, authedFetch } from './hooks/useAuth';
import Register from './pages/Register';
import Login from './pages/Login';
import ForgotPassword from './pages/ForgotPassword';
import Profile from './pages/Profile';
import PickHistory from './pages/PickHistory';
import TwoFAEnroll from './pages/TwoFAEnroll';
import Leaderboard from './pages/Leaderboard';

// ─── Types ───────────────────────────────────────────────────────────────────

type Draw = {
  id: string;
  game: string;
  game_label: string;
  draw_date: string;
  draw_number?: number | null;
  winning_numbers: number[];
  bonus_ball?: number | null;
  jackpot_amount?: string | null;
  jackpot_currency: string;
  scraped_at: string;
};

type ResultsResponse = {
  game: string;
  game_label: string;
  page: number;
  page_size: number;
  total_items: number;
  total_pages: number;
  latest_draw: Draw | null;
  history: Draw[];
  next_draw_at?: string | null;
  disclaimer: string;
  responsible_gambling_url: string;
};

type FrequencyCount = {
  number: number;
  count: number;
  adjusted_pct: number | null;
  eligible_draws: number | null;
};

type PickSet = {
  label: string;
  numbers: number[];
  bonus_ball?: number | null;
  rationale: string;
};

type TopPairStat = {
  numbers: number[];
  count: number;
  pct: number;
};

type DrawInsights = {
  total_draws: number;
  sum_lo: number;
  sum_hi: number;
  sum_avg: number;
  odd_min: number;
  odd_max: number;
  consecutive_pct_zero: number;
  consecutive_pct_one: number;
  consecutive_pct_two_plus: number;
  top_pairs: TopPairStat[];
  reliable_hot: number[];
  recent_hot: number[];
};

type PredictResponse = {
  game: string;
  game_label: string;
  generated_at: string;
  numbers_per_pick: number;
  has_bonus_ball?: boolean;
  bonus_pool_max?: number | null;
  confidence: null;
  expected_value: number;
  latest_draw: Draw | null;
  next_draw_date?: string | null;
  disclaimer: string;
  responsible_gambling_url: string;
  hot_numbers: number[];
  cold_numbers: number[];
  frequency_counts: FrequencyCount[];
  pick_sets: PickSet[];
  insights: DrawInsights | null;
};

type BulkPickResponse = {
  game: string;
  game_label: string;
  strategy: string;
  strategy_label: string;
  strategy_rationale: string;
  count: number;
  numbers_per_pick: number;
  picks: number[][];
  has_bonus_ball?: boolean;
  bonus_balls?: (number | null)[];
  next_draw_date?: string | null;
  generated_at: string;
  disclaimer: string;
};

const BULK_STRATEGIES: { value: string; label: string; gated?: boolean }[] = [
  { value: 'weighted_hybrid', label: 'Weighted hybrid', gated: true },
  { value: 'frequency',       label: 'Frequency pick',  gated: true },
  { value: 'pair_seeded',     label: 'Pair-seeded pick', gated: true },
  { value: 'balanced_random', label: 'Balanced random' },
  { value: 'pure_random',     label: 'Pure random' },
];

const GATED_STRATEGY_LABELS = new Set(['Weighted hybrid', 'Frequency pick', 'Pair-seeded pick']);

// ─── Game Config ─────────────────────────────────────────────────────────────

type GameConfig = {
  label: string;
  ballBg: string;
  tabActiveBg: string;
  tabActiveText: string;
  badgeBg: string;
  badgeText: string;
  barBg: string;
};

const GAME_CONFIG: Record<string, GameConfig> = {
  lotto:            { label: 'Lotto',         ballBg: 'bg-sky-600',      tabActiveBg: 'bg-sky-600',      tabActiveText: 'text-white', badgeBg: 'bg-sky-100',      badgeText: 'text-sky-800',     barBg: 'bg-sky-500'      },
  'lotto-plus-1':   { label: 'Lotto Plus 1',  ballBg: 'bg-violet-600',   tabActiveBg: 'bg-violet-600',   tabActiveText: 'text-white', badgeBg: 'bg-violet-100',   badgeText: 'text-violet-800',  barBg: 'bg-violet-500'   },
  'lotto-plus-2':   { label: 'Lotto Plus 2',  ballBg: 'bg-indigo-600',   tabActiveBg: 'bg-indigo-600',   tabActiveText: 'text-white', badgeBg: 'bg-indigo-100',   badgeText: 'text-indigo-800',  barBg: 'bg-indigo-500'   },
  powerball:        { label: 'Powerball',      ballBg: 'bg-amber-500',    tabActiveBg: 'bg-amber-500',    tabActiveText: 'text-white', badgeBg: 'bg-amber-100',    badgeText: 'text-amber-800',   barBg: 'bg-amber-400'    },
  'powerball-plus': { label: 'Powerball Plus', ballBg: 'bg-rose-600',     tabActiveBg: 'bg-rose-600',     tabActiveText: 'text-white', badgeBg: 'bg-rose-100',     badgeText: 'text-rose-800',    barBg: 'bg-rose-500'     },
  'daily-lotto':    { label: 'Daily Lotto',    ballBg: 'bg-emerald-600',  tabActiveBg: 'bg-emerald-600',  tabActiveText: 'text-white', badgeBg: 'bg-emerald-100',  badgeText: 'text-emerald-800', barBg: 'bg-emerald-500'  },
};

const GAME_SLUGS = Object.keys(GAME_CONFIG);
const DEFAULT_GAME = 'lotto';
const DISCLAIMER = 'Entertainment only. Lottery draws are random and this page does not improve odds.';

// ─── Draw Schedule + Countdown ────────────────────────────────────────────────

// SA draw times in UTC (SAST = UTC+2, no DST).
// days: JS UTC day-of-week (0=Sun … 6=Sat)
const DRAW_SCHEDULE: Record<string, { days: number[]; utcHour: number; utcMinute: number }> = {
  lotto:            { days: [3, 6], utcHour: 18, utcMinute: 57 }, // Wed/Sat 20:57 SAST
  'lotto-plus-1':   { days: [3, 6], utcHour: 18, utcMinute: 57 },
  'lotto-plus-2':   { days: [3, 6], utcHour: 18, utcMinute: 57 },
  powerball:        { days: [2, 5], utcHour: 19, utcMinute: 0  }, // Tue/Fri 21:00 SAST
  'powerball-plus': { days: [2, 5], utcHour: 19, utcMinute: 0  },
  'daily-lotto':    { days: [0, 1, 2, 3, 4, 5, 6], utcHour: 19, utcMinute: 0 }, // Daily 21:00 SAST
};

function getNextDraw(game: string): Date | null {
  const sched = DRAW_SCHEDULE[game];
  if (!sched) return null;
  const now = Date.now();
  for (let daysAhead = 0; daysAhead <= 7; daysAhead++) {
    const d = new Date(now);
    d.setUTCDate(d.getUTCDate() + daysAhead);
    d.setUTCHours(sched.utcHour, sched.utcMinute, 0, 0);
    if (sched.days.includes(d.getUTCDay()) && d.getTime() > now) return d;
  }
  return null;
}

function formatCountdown(target: Date): string {
  const diff = target.getTime() - Date.now();
  if (diff <= 0) return 'Draw starting now';
  const mins = Math.floor(diff / 60000);
  const hrs = Math.floor(mins / 60);
  const rem = mins % 60;
  if (hrs >= 48) return `Draw in ${Math.floor(hrs / 24)}d`;
  if (hrs > 0) return `Draw in ${hrs}h ${rem}m`;
  return `Draw in ${rem}m`;
}

function isDrawSoon(game: string): boolean {
  const next = getNextDraw(game);
  return !!next && next.getTime() - Date.now() <= 3_600_000;
}

function useCountdown(game: string): string | null {
  const [label, setLabel] = useState<string | null>(null);
  useEffect(() => {
    const tick = () => {
      const next = getNextDraw(game);
      setLabel(next ? formatCountdown(next) : null);
    };
    tick();
    const id = setInterval(tick, 30_000);
    return () => clearInterval(id);
  }, [game]);
  return label;
}

// ─── Utilities ────────────────────────────────────────────────────────────────

async function fetchJson<T>(path: string, signal?: AbortSignal): Promise<T> {
  const r = await fetch(path, { signal });
  if (!r.ok) throw new Error(`Request failed: ${r.status} ${r.statusText}`);
  return (await r.json()) as T;
}

function formatMoney(amount: string | null | undefined, currency: string | undefined): string | null {
  if (!amount) return null;
  const n = Number(amount);
  if (Number.isNaN(n)) return null;
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: currency || 'ZAR',
    maximumFractionDigits: 0,
  }).format(n);
}

function formatTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString('en-ZA', {
      hour: '2-digit', minute: '2-digit', timeZone: 'UTC', hour12: false,
    }) + ' UTC';
  } catch {
    return iso;
  }
}

function gameCfg(game: string): GameConfig {
  return GAME_CONFIG[game] ?? GAME_CONFIG[DEFAULT_GAME];
}

// ─── App ─────────────────────────────────────────────────────────────────────

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        <Route path="/results/:game" element={<ResultsPage />} />
        <Route path="/predict/:game" element={<PredictPage />} />
        <Route path="/register" element={<Register />} />
        <Route path="/login" element={<Login />} />
        <Route path="/forgot-password" element={<ForgotPassword />} />
        <Route path="/profile" element={<Profile />} />
        <Route path="/profile/picks" element={<PickHistory />} />
        <Route path="/profile/2fa" element={<TwoFAEnroll />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  );
}

// ─── Landing Page ─────────────────────────────────────────────────────────────

function LandingPage() {
  const { user: currentUser } = useAuth();
  const [selectedGame, setSelectedGame] = useState(DEFAULT_GAME);
  const [results, setResults] = useState<ResultsResponse | null>(null);
  const [prediction, setPrediction] = useState<PredictResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const cfg = gameCfg(selectedGame);
  const drawSoon = isDrawSoon(selectedGame);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setResults(null);
    setPrediction(null);
    Promise.allSettled([
      fetchJson<ResultsResponse>(`/api/v1/results/${selectedGame}?page=1&page_size=3`, ac.signal),
      fetchJson<PredictResponse>(`/api/v1/predict/${selectedGame}`, ac.signal),
    ]).then(([rr, pr]) => {
      if (ac.signal.aborted) return;
      const errs: string[] = [];
      if (rr.status === 'fulfilled') setResults(rr.value);
      else errs.push(rr.reason instanceof Error ? rr.reason.message : String(rr.reason));
      if (pr.status === 'fulfilled') setPrediction(pr.value);
      else errs.push(pr.reason instanceof Error ? pr.reason.message : String(pr.reason));
      if (errs.length) setError(errs.join(' · '));
      setLoading(false);
    });
    return () => ac.abort();
  }, [selectedGame]);

  const hybridPick = prediction?.pick_sets.find((s) => s.label === 'Weighted hybrid') ?? null;

  return (
    <PublicShell
      title="SA Lottery results, picks & honest analysis"
      eyebrow="AI Lotto Predictor (SA)"
      description="Free frequency analysis, hot & cold numbers, and on-demand picks across all 6 SA lottery games. No sign-up required."
      disclaimer={results?.disclaimer ?? DISCLAIMER}
      responsibleLink={results?.responsible_gambling_url ?? 'https://responsiblegambling.org.za/'}
      error={error}
      activeGame={null}
    >
      {/* Game selector */}
      <GameTabStrip activeGame={selectedGame} onSelect={setSelectedGame} linkMode={false} />

      <section className="mt-5 grid gap-5 lg:grid-cols-[1.3fr_0.7fr]">
        {/* Hero */}
        <div className="rounded-[2rem] border border-slate-200 bg-white/90 p-6 shadow-[0_20px_80px_rgba(15,23,42,0.10)] backdrop-blur">
          <span className="inline-flex rounded-full bg-sky-50 px-3 py-1 text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">
            100% free · No sign-up
          </span>
          <h2 className="mt-4 text-3xl font-bold leading-tight text-slate-950 sm:text-5xl">
            Smarter SA lottery<br />
            <span className="text-sky-700">picks. Honest analysis.</span>
          </h2>
          <p className="mt-4 max-w-2xl text-sm leading-7 text-slate-600 sm:text-base">
            Historical frequency analysis, hot & cold numbers, pattern-based and hybrid picks
            across all six South African lottery games — completely free, no registration.
          </p>

          <div className="mt-6 flex flex-wrap gap-3">
            {['6 games', 'Free forever', 'No sign-up', 'Mobile first'].map((t) => (
              <span key={t} className="rounded-full border border-slate-200 bg-slate-50 px-4 py-2 text-xs font-semibold text-slate-700">
                {t}
              </span>
            ))}
          </div>

          {/* Upcoming draws strip — Lotto & Powerball only (no Daily Lotto) */}
          <div className="mt-6">
            <p className="mb-2 text-[0.65rem] font-semibold uppercase tracking-[0.2em] text-slate-400">Upcoming draws</p>
            <div className="grid gap-2 sm:grid-cols-2">
              <NextDrawMini game="lotto" />
              <NextDrawMini game="powerball" />
            </div>
          </div>

          {/* Game grid */}
          <div className="mt-8 grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
            {GAME_SLUGS.map((slug) => {
              const c = gameCfg(slug);
              return (
                <div key={slug} className="rounded-2xl border border-slate-200 bg-slate-50 p-4 transition-all hover:-translate-y-0.5 hover:shadow-md">
                  <div className={`mb-3 h-1.5 w-10 rounded-full ${c.tabActiveBg}`} />
                  <div className="text-sm font-semibold text-slate-900">{c.label}</div>
                  <div className="mt-3 flex gap-2">
                    <Link
                      to={`/results/${slug}`}
                      className="flex-1 cursor-pointer rounded-xl border border-slate-300 bg-white px-3 py-2 text-center text-xs font-semibold text-slate-700 transition hover:border-slate-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                    >
                      Results
                    </Link>
                    <Link
                      to={`/predict/${slug}`}
                      className={`flex-1 cursor-pointer rounded-xl px-3 py-2 text-center text-xs font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${c.tabActiveBg}`}
                    >
                      Pick
                    </Link>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick-pick aside */}
        <div className="flex flex-col gap-5">
          <SummaryCard
            title={drawSoon ? `Today's ${cfg.label} pick` : `${cfg.label} quick pick`}
            subtitle={drawSoon ? 'Draw is soon — get your numbers now' : 'Hybrid analysis pick for selected game'}
          >
            {loading ? (
              <SkeletonBalls count={6} />
            ) : hybridPick && hybridPick.numbers.length > 0 ? (
              <>
                <NumberRow numbers={hybridPick.numbers} bonusBall={hybridPick.bonus_ball ?? undefined} game={selectedGame} />
                <p className="mt-3 text-xs leading-5 text-slate-600">{hybridPick.rationale}</p>
                <Link
                  to={`/predict/${selectedGame}`}
                  className={`mt-4 inline-flex w-full cursor-pointer items-center justify-center rounded-full py-3 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${cfg.tabActiveBg}`}
                >
                  Full prediction surface →
                </Link>
              </>
            ) : currentUser === null ? (
              <GatedPickOverlay />
            ) : (
              <NoHistoryState />
            )}
          </SummaryCard>

          <NextDrawCard game={selectedGame} />

          <SummaryCard title={`Latest ${cfg.label} draw`} subtitle="Most recent published result">
            {loading ? (
              <SkeletonBalls count={6} />
            ) : results?.latest_draw ? (
              <>
                <div className="mb-3 text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">
                  {results.latest_draw.draw_date}
                </div>
                <NumberRow
                  numbers={results.latest_draw.winning_numbers}
                  bonusBall={results.latest_draw.bonus_ball ?? undefined}
                  game={selectedGame}
                />
                {results.latest_draw.jackpot_amount && (
                  <span className={`mt-3 inline-flex rounded-full px-3 py-1 text-xs font-semibold ${cfg.badgeBg} ${cfg.badgeText}`}>
                    Jackpot {formatMoney(results.latest_draw.jackpot_amount, results.latest_draw.jackpot_currency)}
                  </span>
                )}
              </>
            ) : (
              <p className="text-sm text-slate-500">No draw data yet.</p>
            )}
          </SummaryCard>
        </div>
      </section>
    </PublicShell>
  );
}

// ─── Results Page ─────────────────────────────────────────────────────────────

function ResultsPage() {
  const { game = DEFAULT_GAME } = useParams<{ game: string }>();
  const [page, setPage] = useState(1);
  const [data, setData] = useState<ResultsResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Reset page on game change
  useEffect(() => { setPage(1); }, [game]);

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    fetchJson<ResultsResponse>(`/api/v1/results/${game}?page=${page}&page_size=10`, ac.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((err: Error) => { if (err.name !== 'AbortError') { setError(err.message); setLoading(false); } });
    return () => ac.abort();
  }, [game, page]);

  const cfg = gameCfg(game);
  const gameLabel = data?.game_label ?? cfg.label;

  return (
    <PublicShell
      title={`${gameLabel} results`}
      eyebrow="Results"
      description={`Latest ${gameLabel} draw results and full paginated draw history.`}
      disclaimer={data?.disclaimer ?? DISCLAIMER}
      responsibleLink={data?.responsible_gambling_url ?? 'https://responsiblegambling.org.za/'}
      error={error}
      activeGame={game}
    >
      <GameTabStrip activeGame={game} onSelect={null} linkMode basePath="results" />

      <section className="mt-5 grid gap-5 lg:grid-cols-[0.9fr_1.1fr]">
        {/* Left: latest draw + next draw */}
        <div className="flex flex-col gap-5">
          <SummaryCard title={`Latest ${gameLabel} draw`} subtitle="Most recent public result">
            {loading && !data ? (
              <SkeletonBalls count={6} />
            ) : data?.latest_draw ? (
              <>
                <div className="text-sm font-semibold text-slate-900">{data.latest_draw.draw_date}</div>
                <div className="mt-3">
                  <NumberRow
                    numbers={data.latest_draw.winning_numbers}
                    bonusBall={data.latest_draw.bonus_ball ?? undefined}
                    game={game}
                  />
                </div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {data.latest_draw.jackpot_amount && (
                    <span className={`rounded-full px-3 py-1 text-xs font-semibold ${cfg.badgeBg} ${cfg.badgeText}`}>
                      Jackpot {formatMoney(data.latest_draw.jackpot_amount, data.latest_draw.jackpot_currency)}
                    </span>
                  )}
                  {data.latest_draw.draw_number != null && (
                    <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-semibold text-slate-700">
                      Draw #{data.latest_draw.draw_number}
                    </span>
                  )}
                </div>
                <Link
                  to={`/predict/${game}`}
                  className={`mt-5 inline-flex w-full cursor-pointer items-center justify-center rounded-full py-3 text-sm font-semibold text-white transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 ${cfg.tabActiveBg}`}
                >
                  Generate picks →
                </Link>
              </>
            ) : (
              <p className="text-sm text-slate-500">No draw data published yet.</p>
            )}
          </SummaryCard>

          <NextDrawCard game={game} />
        </div>

        {/* History + pagination */}
        <SummaryCard
          title="Draw history"
          subtitle={data ? `Page ${data.page} of ${data.total_pages} · ${data.total_items} draws total` : 'Loading…'}
        >
          {loading && !data ? (
            <div className="grid gap-3">
              {Array.from({ length: 5 }).map((_, i) => (
                <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : (
            <>
              <div className="grid gap-3">
                {(data?.history ?? []).map((draw) => (
                  <article key={draw.id} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="flex flex-wrap items-center justify-between gap-2">
                      <div className="text-sm font-semibold text-slate-900">{draw.draw_date}</div>
                      {draw.draw_number != null && (
                        <div className="text-xs uppercase tracking-[0.18em] text-slate-400">#{draw.draw_number}</div>
                      )}
                    </div>
                    <div className="mt-3">
                      <NumberRow
                        numbers={draw.winning_numbers}
                        bonusBall={draw.bonus_ball ?? undefined}
                        game={game}
                        small
                      />
                    </div>
                  </article>
                ))}
                {data && data.history.length === 0 && (
                  <p className="text-sm text-slate-500">No historical draws loaded yet.</p>
                )}
              </div>

              {/* Pagination */}
              {data && data.total_pages > 1 && (
                <div className="mt-5 flex items-center justify-between border-t border-slate-100 pt-4">
                  <button
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page <= 1}
                    aria-label="Previous page"
                    className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    ← Prev
                  </button>
                  <span className="text-xs text-slate-500">{data.page} / {data.total_pages}</span>
                  <button
                    onClick={() => setPage((p) => Math.min(data.total_pages, p + 1))}
                    disabled={page >= data.total_pages}
                    aria-label="Next page"
                    className="inline-flex min-h-10 cursor-pointer items-center gap-1.5 rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    Next →
                  </button>
                </div>
              )}
            </>
          )}
        </SummaryCard>
      </section>
    </PublicShell>
  );
}

// ─── Predict Page ─────────────────────────────────────────────────────────────

function PredictPage() {
  const { game = DEFAULT_GAME } = useParams<{ game: string }>();
  const { user: currentUser } = useAuth();
  const [fetchKey, setFetchKey] = useState(0);
  const [data, setData] = useState<PredictResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  // Bulk picks state
  const [bulkStrategy, setBulkStrategy] = useState('weighted_hybrid');
  const [bulkCount, setBulkCount] = useState(5);
  const [bulkData, setBulkData] = useState<BulkPickResponse | null>(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkError, setBulkError] = useState<string | null>(null);

  const countdown = useCountdown(game);
  const drawSoon = isDrawSoon(game);
  const cfg = gameCfg(game);
  const gameLabel = data?.game_label ?? cfg.label;
  const maxAdjPct = Math.max(...(data?.frequency_counts?.map((f) => f.adjusted_pct ?? 0) ?? []), 1);
  const isGatedBulk = BULK_STRATEGIES.find((s) => s.value === bulkStrategy)?.gated ?? false;

  useEffect(() => {
    const ac = new AbortController();
    setLoading(true);
    setError(null);
    setData(null);
    fetchJson<PredictResponse>(`/api/v1/predict/${game}`, ac.signal)
      .then((d) => { setData(d); setLoading(false); })
      .catch((err: Error) => { if (err.name !== 'AbortError') { setError(err.message); setLoading(false); } });
    return () => ac.abort();
  }, [game, fetchKey]);

  function handleGenerate() {
    setData(null);
    setLoading(true);
    setBulkData(null);
    setFetchKey((k) => k + 1);
  }

  function handleBulkGenerate() {
    if (isGatedBulk && !currentUser) return;
    setBulkData(null);
    setBulkError(null);
    setBulkLoading(true);
    fetch(`/api/v1/predict/${game}/bulk?strategy=${bulkStrategy}&count=${bulkCount}`, { credentials: 'include' })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Request failed: ${r.status} ${r.statusText}`);
        return r.json() as Promise<BulkPickResponse>;
      })
      .then((d) => { setBulkData(d); setBulkLoading(false); })
      .catch((err: Error) => { setBulkError(err.message); setBulkLoading(false); });
  }

  return (
    <PublicShell
      title={`${gameLabel} prediction surface`}
      eyebrow="Prediction"
      description={`On-demand ${gameLabel} pick sets with frequency analysis and historical context. Entertainment only.`}
      disclaimer={data?.disclaimer ?? DISCLAIMER}
      responsibleLink={data?.responsible_gambling_url ?? 'https://responsiblegambling.org.za/'}
      error={error}
      activeGame={game}
    >
      <GameTabStrip activeGame={game} onSelect={null} linkMode basePath="predict" />

      {/* Generate bar */}
      <div className="mt-5 flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex flex-col gap-0.5">
          {data?.generated_at && (
            <p className="text-xs text-slate-400">Generated at {formatTime(data.generated_at)}</p>
          )}
          {countdown && (
            <p className={`text-xs font-semibold ${drawSoon ? 'text-amber-700' : 'text-slate-500'}`}>
              {countdown}
            </p>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={loading}
          aria-label="Generate new pick set"
          className={`inline-flex min-h-11 cursor-pointer items-center gap-2 rounded-full px-6 py-2.5 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-95 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${drawSoon ? 'bg-amber-500' : cfg.tabActiveBg}`}
        >
          {/* Refresh icon */}
          <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
          </svg>
          {loading ? 'Generating…' : drawSoon ? "Generate today's pick" : 'Generate new pick'}
        </button>
      </div>

      <section className="mt-4 grid gap-5 lg:grid-cols-[1fr_0.9fr]">
        {/* Pick sets */}
        <SummaryCard title={`${gameLabel} pick sets`} subtitle="Honest statistical analysis — not a winning guarantee.">
          {loading ? (
            <div className="grid gap-3">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="h-24 animate-pulse rounded-2xl bg-slate-100" />
              ))}
            </div>
          ) : (
            <div className="grid gap-3">
              {(data?.pick_sets ?? []).map((set, idx) => {
                const isGated = GATED_STRATEGY_LABELS.has(set.label) && !currentUser;
                return (
                  <article key={idx} className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                    <div className="mb-3 flex items-center justify-between gap-3">
                      <h3 className="text-sm font-semibold text-slate-950">{set.label}</h3>
                      {set.numbers.length > 0 && (
                        <span className={`rounded-full px-2 py-0.5 text-[0.68rem] font-semibold uppercase tracking-wide ${cfg.badgeBg} ${cfg.badgeText}`}>
                          {set.numbers.length} + bonus
                        </span>
                      )}
                      {isGated && <LockBadge />}
                    </div>
                    {set.numbers.length > 0 ? (
                      <>
                        <NumberRow numbers={set.numbers} bonusBall={set.bonus_ball ?? undefined} game={game} />
                        <div className="mt-3">
                          <SavePickButton
                            game={game}
                            drawDate={data?.next_draw_date ?? null}
                            numbers={set.numbers}
                            bonusBall={set.bonus_ball ?? null}
                            strategy={set.label.toLowerCase().replace(/ /g, '_')}
                          />
                        </div>
                      </>
                    ) : isGated ? (
                      <GatedPickOverlay />
                    ) : (
                      <NoHistoryState compact />
                    )}
                    {!isGated && set.rationale && (
                      <p className="mt-3 text-xs leading-5 text-slate-500">{set.rationale}</p>
                    )}
                  </article>
                );
              })}
            </div>
          )}
        </SummaryCard>

        <div className="grid gap-5 content-start">

          {/* Hot numbers & recent form */}
          {(data?.insights?.reliable_hot?.length || data?.insights?.recent_hot?.length) ? (
            <SummaryCard title="Hot numbers" subtitle="Pool-adjusted frequency — numbers with enough history to trust">
              {data.insights!.reliable_hot.length > 0 && (
                <div className="mb-3">
                  <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-amber-600">All-time hot</p>
                  <NumberRow numbers={data.insights!.reliable_hot} game={game} />
                </div>
              )}
              {data.insights!.recent_hot.length > 0 && (
                <div>
                  <p className="mb-1.5 text-[0.65rem] font-semibold uppercase tracking-[0.18em] text-rose-500">Recent over-performers (last 52 draws)</p>
                  <NumberRow numbers={data.insights!.recent_hot} game={game} />
                </div>
              )}
              {data.insights!.recent_hot.length === 0 && (
                <p className="text-xs text-slate-400">No numbers are significantly over-performing in recent draws.</p>
              )}
            </SummaryCard>
          ) : null}

          {/* Top co-drawn pairs */}
          {data?.insights?.top_pairs?.length ? (
            <SummaryCard title="Most co-drawn pairs" subtitle={`Numbers that appear together most often across ${data.insights!.total_draws.toLocaleString()} draws`}>
              <div className="grid gap-2">
                {data.insights!.top_pairs.map((pair, i) => (
                  <div key={i} className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-2">
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white ${gameCfg(game).ballBg}`}>
                        {pair.numbers[0]}
                      </span>
                      <span className="text-xs font-semibold text-slate-400">+</span>
                      <span className={`inline-flex h-7 w-7 items-center justify-center rounded-full text-xs font-bold text-white ${gameCfg(game).ballBg}`}>
                        {pair.numbers[1]}
                      </span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="h-2 w-24 overflow-hidden rounded-full bg-slate-100">
                        <div
                          className={`h-full rounded-full ${gameCfg(game).barBg} opacity-70`}
                          style={{ width: `${Math.min(pair.pct / (data.insights!.top_pairs[0].pct || 1) * 100, 100)}%` }}
                        />
                      </div>
                      <span className="w-20 text-right text-xs tabular-nums text-slate-500">
                        {pair.count}× ({pair.pct.toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </SummaryCard>
          ) : null}

          {/* Draw pattern hints */}
          {data?.insights && (
            <SummaryCard title="Draw pattern hints" subtitle="What 50 years of draws tell us about structure">
              <div className="grid gap-2.5 text-xs">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">Sum of winning numbers</span>
                  <span className="font-semibold tabular-nums text-slate-900">
                    {data.insights.sum_lo}–{data.insights.sum_hi} <span className="font-normal text-slate-400">(avg {data.insights.sum_avg})</span>
                  </span>
                </div>
                <div className="flex items-center justify-between gap-2">
                  <span className="text-slate-600">Odd numbers per draw</span>
                  <span className="font-semibold tabular-nums text-slate-900">
                    {data.insights.odd_min}–{data.insights.odd_max} odd
                  </span>
                </div>
                <div className="h-px bg-slate-100" />
                <p className="text-[0.7rem] font-semibold uppercase tracking-[0.15em] text-slate-400">Consecutive number pairs</p>
                <div className="grid gap-1">
                  {[
                    { label: 'No consecutive pairs', pct: data.insights.consecutive_pct_zero, tip: 'most common' },
                    { label: 'One consecutive pair', pct: data.insights.consecutive_pct_one, tip: 'common' },
                    { label: 'Two or more', pct: data.insights.consecutive_pct_two_plus, tip: 'rare' },
                  ].map(({ label, pct, tip }) => (
                    <div key={label} className="flex items-center gap-2">
                      <span className="w-36 shrink-0 text-slate-600">{label}</span>
                      <div className="flex-1 overflow-hidden rounded-full bg-slate-100 h-2">
                        <div
                          className={`h-full rounded-full ${pct < 15 ? 'bg-rose-400' : 'bg-emerald-400'}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                      <span className="w-16 text-right tabular-nums text-slate-500">{pct}%</span>
                      <span className="w-16 text-right text-slate-400 italic">{tip}</span>
                    </div>
                  ))}
                </div>
                <p className="text-[0.68rem] text-slate-400 leading-4">
                  Hint: picks with 0–1 consecutive pairs match {(data.insights.consecutive_pct_zero + data.insights.consecutive_pct_one).toFixed(0)}% of all historical draws.
                </p>
              </div>
            </SummaryCard>
          )}

          {/* Frequency bar chart */}
          <SummaryCard title="Frequency analysis" subtitle="Pool-adjusted rate per eligible draw">
            {loading ? (
              <div className="grid gap-2">
                {Array.from({ length: 8 }).map((_, i) => (
                  <div key={i} className="h-7 animate-pulse rounded-full bg-slate-100" />
                ))}
              </div>
            ) : data?.frequency_counts?.length ? (
              <div className="grid gap-1.5 overflow-y-auto max-h-80">
                {data.frequency_counts.map((item) => {
                  const adjPct = item.adjusted_pct ?? 0;
                  const barPct = Math.round((adjPct / maxAdjPct) * 100);
                  const isHot = (data.hot_numbers ?? []).includes(item.number);
                  const isCold = (data.cold_numbers ?? []).includes(item.number);
                  return (
                    <div key={item.number} className="flex items-center gap-2.5">
                      <span className="w-6 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-700">
                        {item.number}
                      </span>
                      <div className="flex-1 overflow-hidden rounded-full bg-slate-100 h-4">
                        <div
                          className={`h-full rounded-full ${cfg.barBg} ${isHot ? '' : 'opacity-60'} transition-all`}
                          style={{ width: `${Math.max(barPct, 3)}%` }}
                          role="meter"
                          aria-valuenow={adjPct}
                          aria-valuemin={0}
                          aria-valuemax={maxAdjPct}
                          aria-label={`Number ${item.number}: ${item.count} draws (${adjPct.toFixed(1)}% pool-adjusted)`}
                        />
                      </div>
                      <span className="w-14 shrink-0 text-right text-[0.68rem] tabular-nums text-slate-400">
                        {adjPct > 0 ? `${adjPct.toFixed(1)}%` : `${item.count}×`}
                      </span>
                      <span className="w-8 shrink-0 text-[0.65rem] font-bold">
                        {isHot ? <span className="text-amber-600">HOT</span> : isCold ? <span className="text-sky-500">COLD</span> : null}
                      </span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-sm text-slate-500">No frequency data — draw history not yet loaded.</p>
            )}
          </SummaryCard>

          {/* Latest draw context */}
          <SummaryCard title="Latest draw context" subtitle="Reference point for this prediction">
            {loading ? (
              <SkeletonBalls count={6} />
            ) : data?.latest_draw ? (
              <>
                <div className="text-sm font-semibold text-slate-900">{data.latest_draw.draw_date}</div>
                <div className="mt-3">
                  <NumberRow
                    numbers={data.latest_draw.winning_numbers}
                    bonusBall={data.latest_draw.bonus_ball ?? undefined}
                    game={game}
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-slate-500">No draw history yet — scraper not yet run.</p>
            )}
          </SummaryCard>
        </div>
      </section>

      {/* ── Bulk picks ─────────────────────────────────────────────────── */}
      <section className="mt-5">
        <SummaryCard
          title="Generate bulk picks"
          subtitle="Choose a strategy and quantity — get multiple unique combinations in one shot"
        >
          {/* Controls */}
          <div className="flex flex-wrap items-center gap-3">
            {/* Strategy selector */}
            <select
              value={bulkStrategy}
              onChange={(e) => { setBulkStrategy(e.target.value); setBulkData(null); }}
              className="cursor-pointer rounded-xl border border-slate-200 bg-white px-3 py-2.5 text-sm font-medium text-slate-800 shadow-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
              aria-label="Pick strategy"
            >
              {BULK_STRATEGIES.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}{s.gated ? ' 🔒' : ''}
                </option>
              ))}
            </select>

            {/* Count stepper */}
            <div className="flex items-center gap-1 rounded-xl border border-slate-200 bg-white shadow-sm overflow-hidden">
              <button
                onClick={() => setBulkCount((c) => Math.max(1, c - 1))}
                aria-label="Decrease count"
                className="flex h-10 w-10 cursor-pointer items-center justify-center text-slate-500 transition hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 12h14" />
                </svg>
              </button>
              <span className="w-16 text-center text-sm font-semibold tabular-nums text-slate-800">
                {bulkCount} {bulkCount === 1 ? 'pick' : 'picks'}
              </span>
              <button
                onClick={() => setBulkCount((c) => Math.min(20, c + 1))}
                aria-label="Increase count"
                className="flex h-10 w-10 cursor-pointer items-center justify-center text-slate-500 transition hover:bg-slate-50 active:bg-slate-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 5v14M5 12h14" />
                </svg>
              </button>
            </div>

            {/* Generate button — locked if gated strategy and not signed in */}
            {isGatedBulk && !currentUser ? (
              <Link
                to="/login"
                className="inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full bg-slate-700 px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
                </svg>
                Sign in to unlock
              </Link>
            ) : (
              <button
                onClick={handleBulkGenerate}
                disabled={bulkLoading}
                className={`inline-flex min-h-10 cursor-pointer items-center gap-2 rounded-full px-5 py-2 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 active:scale-95 disabled:opacity-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 ${cfg.tabActiveBg}`}
              >
                <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M16.023 9.348h4.992v-.001M2.985 19.644v-4.992m0 0h4.992m-4.993 0 3.181 3.183a8.25 8.25 0 0 0 13.803-3.7M4.031 9.865a8.25 8.25 0 0 1 13.803-3.7l3.181 3.182m0-4.991v4.99" />
                </svg>
                {bulkLoading ? 'Generating…' : 'Generate'}
              </button>
            )}
          </div>

          {/* Results */}
          {bulkError && (
            <p className="mt-4 text-sm text-rose-600">{bulkError}</p>
          )}
          {bulkLoading && (
            <div className="mt-4 grid gap-2">
              {Array.from({ length: bulkCount }).map((_, i) => (
                <div key={i} className="h-10 animate-pulse rounded-xl bg-slate-100" />
              ))}
            </div>
          )}
          {!bulkLoading && bulkData?.picks?.length ? (
            <div className="mt-4">
              <p className="mb-3 text-xs leading-5 text-slate-500">{bulkData.strategy_rationale}</p>
              <div className="grid gap-2">
                {bulkData.picks.map((pick, i) => (
                  <div key={i} className="flex flex-wrap items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2">
                    <span className="w-5 shrink-0 text-right text-xs font-semibold tabular-nums text-slate-400">
                      {i + 1}
                    </span>
                    <NumberRow
                      numbers={pick}
                      bonusBall={bulkData.bonus_balls?.[i] ?? undefined}
                      game={game}
                    />
                    <SavePickButton
                      game={game}
                      drawDate={bulkData.next_draw_date ?? null}
                      numbers={pick}
                      bonusBall={bulkData.bonus_balls?.[i] ?? null}
                      strategy={bulkData.strategy}
                    />
                  </div>
                ))}
              </div>
              <p className="mt-3 text-[0.68rem] text-slate-400">
                Generated at {formatTime(bulkData.generated_at)} · {bulkData.count} unique {bulkData.strategy_label} picks
              </p>
            </div>
          ) : null}
        </SummaryCard>
      </section>
    </PublicShell>
  );
}

// ─── Verification Banner ──────────────────────────────────────────────────────

function VerificationBanner({ deadline, email }: { deadline: string | null; email: string }) {
  const navigate = useNavigate();
  const [sending, setSending] = useState(false);

  const hoursLeft = deadline
    ? Math.max(0, Math.floor((new Date(deadline).getTime() - Date.now()) / 3_600_000))
    : null;
  const expired = hoursLeft !== null && hoursLeft <= 0;

  async function handleVerifyNow() {
    setSending(true);
    try {
      await fetch('/api/v1/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
    } finally {
      setSending(false);
      navigate('/register', { state: { step: 'verify', email } });
    }
  }

  return (
    <div className={`mb-4 rounded-2xl border px-4 py-3 text-sm flex items-center justify-between gap-4 ${expired ? 'border-rose-300 bg-rose-50 text-rose-800' : 'border-amber-300 bg-amber-50 text-amber-800'}`}>
      <span>
        {expired
          ? 'Your account is suspended — please verify your email to continue.'
          : `Please verify your email address. Your account will be suspended in ${hoursLeft}h if you don't.`}
      </span>
      <button
        onClick={handleVerifyNow}
        disabled={sending}
        className="shrink-0 font-semibold underline underline-offset-2 hover:opacity-75 disabled:opacity-50"
      >
        {sending ? 'Sending…' : 'Verify now'}
      </button>
    </div>
  );
}

// ─── Public Shell ─────────────────────────────────────────────────────────────

function PublicShell({
  title,
  eyebrow,
  description,
  disclaimer,
  responsibleLink,
  error,
  activeGame,
  children,
}: {
  title: string;
  eyebrow: string;
  description: string;
  disclaimer: string;
  responsibleLink: string;
  error: string | null;
  activeGame: string | null;
  children: ReactNode;
}) {
  const { user, logout } = useAuth();

  useEffect(() => {
    document.title = `${title} | AI Lotto Predictor (SA)`;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', description);
  }, [description, title]);

  return (
    <div className="min-h-screen text-slate-950">
      <div className="mx-auto flex min-h-screen w-full max-w-7xl flex-col px-4 py-4 sm:px-6 lg:px-8">
        <header className="mb-5 rounded-[2rem] border border-slate-200 bg-white/85 px-5 py-4 shadow-[0_18px_60px_rgba(15,23,42,0.08)] backdrop-blur">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs font-semibold uppercase tracking-[0.24em] text-sky-700">{eyebrow}</span>
                {activeGame && <CountdownBadge game={activeGame} />}
              </div>
              <Link to="/" className="mt-1 block focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded">
                <h1 className="cursor-pointer truncate font-bold text-xl text-slate-950 transition-colors hover:text-sky-700 sm:text-2xl">
                  {title}
                </h1>
              </Link>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-slate-500">{description}</p>
            </div>
            <div className="flex shrink-0 flex-wrap gap-2">
              <Link
                to="/"
                className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
              >
                Home
              </Link>
              {activeGame && (
                <Link
                  to={`/predict/${activeGame}`}
                  className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                >
                  Get picks →
                </Link>
              )}
              {user ? (
                <>
                  <Link
                    to="/profile"
                    className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full border border-sky-300 bg-sky-50 px-4 py-2 text-sm font-semibold text-sky-700 transition hover:bg-sky-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    Profile
                  </Link>
                  <button
                    onClick={() => logout()}
                    className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    Sign out
                  </button>
                </>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full border border-slate-300 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:border-sky-300 hover:bg-sky-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    Sign in
                  </Link>
                  <Link
                    to="/register"
                    className="inline-flex min-h-10 cursor-pointer items-center justify-center rounded-full bg-sky-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-sky-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500"
                  >
                    Register free
                  </Link>
                </>
              )}
            </div>
          </div>
        </header>

        <main className="flex-1">
          {error && <ErrorBanner message={error} />}
          {user && !user.is_verified && <VerificationBanner deadline={user.verification_deadline} email={user.email} />}
          {children}
        </main>

        <footer className="mt-8 rounded-[2rem] border border-slate-200 bg-white/85 px-5 py-4 text-sm text-slate-600 shadow-[0_18px_60px_rgba(15,23,42,0.06)] backdrop-blur">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <p className="max-w-3xl leading-6">{disclaimer}</p>
            <a
              href={responsibleLink}
              target="_blank"
              rel="noreferrer"
              className="shrink-0 cursor-pointer font-semibold text-sky-700 underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500 rounded"
            >
              Responsible gambling ↗
            </a>
          </div>
        </footer>
      </div>
    </div>
  );
}

// ─── Game Tab Strip ───────────────────────────────────────────────────────────

function GameTabStrip({
  activeGame,
  onSelect,
  linkMode,
  basePath,
}: {
  activeGame: string;
  onSelect: ((game: string) => void) | null;
  linkMode: boolean;
  basePath?: string;
}) {
  const baseBtn = 'inline-flex shrink-0 min-h-10 cursor-pointer items-center whitespace-nowrap rounded-full px-4 py-2 text-xs font-semibold transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-500';
  const inactiveClass = 'border border-slate-200 bg-white text-slate-700 hover:border-slate-300 hover:bg-slate-50';

  return (
    <nav
      className="mt-2 flex gap-2 overflow-x-auto pb-1"
      aria-label="Select lottery game"
    >
      {GAME_SLUGS.map((slug) => {
        const c = gameCfg(slug);
        const isActive = slug === activeGame;
        const activeClass = `${c.tabActiveBg} ${c.tabActiveText} shadow-sm`;

        if (linkMode && basePath) {
          return (
            <Link
              key={slug}
              to={`/${basePath}/${slug}`}
              aria-current={isActive ? 'page' : undefined}
              className={`${baseBtn} ${isActive ? activeClass : inactiveClass}`}
            >
              {c.label}
            </Link>
          );
        }
        return (
          <button
            key={slug}
            aria-pressed={isActive}
            onClick={() => onSelect?.(slug)}
            className={`${baseBtn} ${isActive ? activeClass : inactiveClass}`}
          >
            {c.label}
          </button>
        );
      })}
    </nav>
  );
}

// ─── Countdown Badge ──────────────────────────────────────────────────────────

function CountdownBadge({ game }: { game: string }) {
  const label = useCountdown(game);
  if (!label) return null;
  const soon = isDrawSoon(game);
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[0.65rem] font-semibold ${soon ? 'bg-amber-100 text-amber-700' : 'bg-slate-100 text-slate-500'}`}
      aria-live="polite"
    >
      {/* Clock icon */}
      <svg className="h-3 w-3 shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <path strokeLinecap="round" d="M12 6v6l4 2" />
      </svg>
      {label}
    </span>
  );
}

// ─── Next Draw Card (sidebar) ─────────────────────────────────────────────────

const CALENDAR_ICON = (
  <svg className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
  </svg>
);

function sastTime(d: Date): string {
  const h = d.getUTCHours() + 2;
  const m = String(d.getUTCMinutes()).padStart(2, '0');
  return `${h}:${m} SAST`;
}

function NextDrawCard({ game }: { game: string }) {
  const countdown = useCountdown(game);
  const next = getNextDraw(game);
  const soon = isDrawSoon(game);
  const cfg = gameCfg(game);
  if (!next) return null;
  const dayName = next.toLocaleDateString('en-ZA', { weekday: 'long', timeZone: 'UTC' });
  const dateStr = next.toLocaleDateString('en-ZA', { day: 'numeric', month: 'long', timeZone: 'UTC' });
  return (
    <SummaryCard title={`Next ${cfg.label} draw`} subtitle={countdown ?? 'Calculating…'}>
      <div className={`flex items-center gap-3 rounded-2xl p-3 ${soon ? 'border border-amber-200 bg-amber-50' : 'bg-slate-50'}`}>
        <div className={`flex h-12 w-12 shrink-0 items-center justify-center rounded-xl text-white ${soon ? 'bg-amber-500' : cfg.tabActiveBg}`}>
          {CALENDAR_ICON}
        </div>
        <div>
          <div className="text-sm font-semibold text-slate-900">{dayName}, {dateStr}</div>
          <div className={`mt-0.5 text-xs ${soon ? 'font-semibold text-amber-700' : 'text-slate-500'}`}>{sastTime(next)}</div>
        </div>
      </div>
    </SummaryCard>
  );
}

// ─── Next Draw Mini (home banner strip) ───────────────────────────────────────

function NextDrawMini({ game }: { game: string }) {
  const countdown = useCountdown(game);
  const next = getNextDraw(game);
  const soon = isDrawSoon(game);
  const cfg = gameCfg(game);
  if (!next) return null;
  const dayName = next.toLocaleDateString('en-ZA', { weekday: 'short', timeZone: 'UTC' });
  const dateStr = next.toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', timeZone: 'UTC' });
  return (
    <div className={`flex items-center gap-3 rounded-2xl border p-3 transition-all hover:-translate-y-0.5 hover:shadow-sm ${soon ? 'border-amber-200 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
      <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-white ${soon ? 'bg-amber-500' : cfg.tabActiveBg}`}>
        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6.75 3v2.25M17.25 3v2.25M3 18.75V7.5a2.25 2.25 0 0 1 2.25-2.25h13.5A2.25 2.25 0 0 1 21 7.5v11.25m-18 0A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75m-18 0v-7.5A2.25 2.25 0 0 1 5.25 9h13.5A2.25 2.25 0 0 1 21 11.25v7.5" />
        </svg>
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-1.5">
          <span className={`rounded-full px-2 py-0.5 text-[0.6rem] font-semibold uppercase tracking-wide ${cfg.badgeBg} ${cfg.badgeText}`}>
            {cfg.label}
          </span>
        </div>
        <div className="mt-0.5 text-xs font-semibold text-slate-800">{dayName} {dateStr} · {sastTime(next)}</div>
        <div className={`text-[0.65rem] ${soon ? 'font-semibold text-amber-600' : 'text-slate-400'}`}>{countdown}</div>
      </div>
    </div>
  );
}

// ─── Summary Card ─────────────────────────────────────────────────────────────

function SummaryCard({ title, subtitle, children }: { title: string; subtitle: string; children: ReactNode }) {
  return (
    <section className="rounded-[2rem] border border-slate-200 bg-white/90 p-5 shadow-[0_18px_70px_rgba(15,23,42,0.08)] backdrop-blur">
      <div className="mb-4">
        <h2 className="text-base font-semibold text-slate-950">{title}</h2>
        <p className="mt-0.5 text-xs leading-5 text-slate-500">{subtitle}</p>
      </div>
      {children}
    </section>
  );
}

// ─── Number Row ───────────────────────────────────────────────────────────────

function NumberRow({
  numbers,
  bonusBall,
  game,
  small,
}: {
  numbers: number[];
  bonusBall?: number;
  game: string;
  small?: boolean;
}) {
  const cfg = gameCfg(game);
  const size = small ? 'h-8 w-8 text-xs' : 'h-11 w-11 text-sm';
  return (
    <div className="flex flex-wrap gap-2" role="list" aria-label="Lottery numbers">
      {numbers.map((n, idx) => (
        <span
          key={`${n}-${idx}`}
          role="listitem"
          className={`inline-flex items-center justify-center rounded-full font-semibold text-white shadow-sm ${size} ${cfg.ballBg}`}
        >
          {n}
        </span>
      ))}
      {typeof bonusBall === 'number' && (
        <span
          role="listitem"
          aria-label={`Bonus ball ${bonusBall}`}
          className={`inline-flex items-center justify-center rounded-full border-2 border-amber-300 bg-amber-50 font-semibold text-amber-900 shadow-sm ${size}`}
        >
          +{bonusBall}
        </span>
      )}
    </div>
  );
}

// ─── Skeleton Balls ───────────────────────────────────────────────────────────

function SkeletonBalls({ count }: { count: number }) {
  return (
    <div className="flex flex-wrap gap-2" aria-label="Loading" aria-busy="true">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="h-11 w-11 animate-pulse rounded-full bg-slate-200" />
      ))}
    </div>
  );
}

// ─── Error Banner ─────────────────────────────────────────────────────────────

function ErrorBanner({ message }: { message: string }) {
  return (
    <div
      role="alert"
      className="mb-5 flex items-start gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      {/* Warning icon */}
      <svg className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126ZM12 15.75h.007v.008H12v-.008Z" />
      </svg>
      <span>{message}</span>
    </div>
  );
}

// ─── No History State ─────────────────────────────────────────────────────────

function NoHistoryState({ compact }: { compact?: boolean }) {
  return (
    <div className={`rounded-xl border border-dashed border-slate-300 bg-slate-50 text-center ${compact ? 'px-3 py-3' : 'px-4 py-5'}`}>
      <p className="text-xs font-medium text-slate-500">No draw history available yet</p>
      <p className="mt-0.5 text-xs text-slate-400">The scraper hasn't run for this game.</p>
    </div>
  );
}

// ─── Gated Pick Overlay ───────────────────────────────────────────────────────

function GatedPickOverlay() {
  return (
    <div className="flex flex-col items-center gap-2 rounded-xl border border-dashed border-slate-300 bg-slate-50 px-4 py-5 text-center">
      <svg className="h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>
      <p className="text-xs font-medium text-slate-600">Registered users only</p>
      <div className="flex gap-2">
        <Link to="/register" className="rounded-full bg-sky-600 px-4 py-1.5 text-xs font-semibold text-white hover:bg-sky-700">
          Register free
        </Link>
        <Link to="/login" className="rounded-full border border-slate-300 px-4 py-1.5 text-xs font-semibold text-slate-700 hover:bg-slate-50">
          Sign in
        </Link>
      </div>
    </div>
  );
}

// ─── Lock Badge ───────────────────────────────────────────────────────────────

function LockBadge() {
  return (
    <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 px-2 py-0.5 text-[0.65rem] font-semibold text-slate-500">
      <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 1 0-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 0 0 2.25-2.25v-6.75a2.25 2.25 0 0 0-2.25-2.25H6.75a2.25 2.25 0 0 0-2.25 2.25v6.75a2.25 2.25 0 0 0 2.25 2.25Z" />
      </svg>
      Members only
    </span>
  );
}

// ─── Save Pick Button ─────────────────────────────────────────────────────────

function SavePickButton({
  game,
  drawDate,
  numbers,
  bonusBall,
  strategy,
}: {
  game: string;
  drawDate: string | null;
  numbers: number[];
  bonusBall: number | null;
  strategy: string;
}) {
  const { user } = useAuth();
  const [saved, setSaved] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  if (!user || !drawDate) return null;

  async function handleSave() {
    setSaving(true);
    setErr('');
    // Convert URL slug (powerball-plus) → backend enum (POWERBALL_PLUS)
    const backendGame = game.toUpperCase().replace(/-/g, '_');
    try {
      const r = await authedFetch('/api/v1/picks', {
        method: 'POST',
        body: JSON.stringify({ game: backendGame, draw_date: drawDate, numbers, bonus_ball: bonusBall, strategy }),
      });
      if (r.ok) {
        setSaved(true);
      } else {
        const d = await r.json().catch(() => ({}));
        setErr((d as { detail?: string }).detail ?? 'Could not save.');
      }
    } catch {
      setErr('Network error.');
    } finally {
      setSaving(false);
    }
  }

  if (saved) {
    return (
      <span className="inline-flex items-center gap-1 rounded-full bg-green-100 px-3 py-1 text-xs font-semibold text-green-700">
        <svg className="h-3 w-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
        Saved
      </span>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      <button
        onClick={handleSave}
        disabled={saving}
        className="inline-flex items-center gap-1 rounded-full border border-sky-300 bg-sky-50 px-3 py-1 text-xs font-semibold text-sky-700 transition hover:bg-sky-100 disabled:opacity-50"
      >
        {saving ? 'Saving…' : 'I played this'}
      </button>
      {err && <p className="text-[0.65rem] text-rose-600">{err}</p>}
    </div>
  );
}

export default App;
